import React from 'react';
import type { Point } from '../types/biApp';
import { formatMoney, formatNumber } from './formatting';

type KpiTone = 'pos' | 'neg' | 'warn' | 'neutral';

export function Kpi({
    label,
    value,
    note,
    loading,
    tone,
    delta,
    locale = 'hr',
}: {
    label: string;
    value?: number | string;
    note?: string;
    loading?: boolean;
    tone?: KpiTone;
    delta?: { text: string; tone?: KpiTone };
    locale?: 'hr' | 'en';
}) {
    const valueClass = `kpi-value${tone ? ` tone-${tone}` : ''}`;
    const deltaGlyph = delta?.tone === 'pos' ? '▲ ' : delta?.tone === 'neg' ? '▼ ' : delta?.tone === 'warn' ? '⚠ ' : '';

    return (
        <article className="card">
            <p className="muted">{label}</p>
            {loading ? (
                <>
                    <div className="kpi-skeleton" aria-hidden="true" />
                    <p className="muted">{locale === 'en' ? 'Loading for selected period…' : 'Učitavanje za odabrani period…'}</p>
                </>
            ) : (
                <>
                    <p className={valueClass}>{typeof value === 'number' ? formatNumber(value, locale) : value}</p>
                    {delta ? (
                        <p className={`kpi-delta${delta.tone ? ` tone-${delta.tone}` : ''}`}>{deltaGlyph}{delta.text}</p>
                    ) : note ? (
                        <p className="muted">{note}</p>
                    ) : null}
                </>
            )}
        </article>
    );
}

export function BalanceCard({ label, value }: { label: string; value: number }) {
    return (
        <article>
            <span>{label}</span>
            <strong>{formatMoney(value)}</strong>
        </article>
    );
}

/**
 * Polished line chart sa Y-axis grid + labels + tooltips po točkama.
 * Više linija (multi-series). Forecast linija je dashed (style: 'dashed').
 */
export function NiceLineChart({
    series, height = 240, locale = 'hr', xLabels = [], yFormatter,
}: {
    series: Array<{ name: string; color: string; values: Array<number | null>; style?: 'solid' | 'dashed' }>;
    height?: number; locale?: 'hr' | 'en'; xLabels?: string[];
    yFormatter?: (n: number) => string;
}) {
    const allVals = series.flatMap((s) => s.values.filter((v): v is number => v != null));
    if (allVals.length === 0) return <div style={{ padding: 20, color: '#999', fontStyle: 'italic' }}>Nema podataka.</div>;
    const maxLen = Math.max(...series.map((s) => s.values.length));
    const maxY = Math.max(1, ...allVals);
    const minY = 0;
    const w = 800; const padL = 70; const padR = 12; const padT = 12; const padB = 28;
    const innerW = w - padL - padR;
    const innerH = height - padT - padB;
    const xStep = innerW / Math.max(maxLen - 1, 1);

    const fmt = yFormatter ?? ((n: number) => n >= 1e6 ? (n/1e6).toFixed(1)+'M' : n >= 1e3 ? (n/1e3).toFixed(0)+'k' : String(Math.round(n)));

    const ticks = [0, 0.25, 0.5, 0.75, 1];
    return (
        <svg viewBox={`0 0 ${w} ${height}`} style={{ width: '100%', height, fontFamily: 'inherit', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet">
            {/* Y grid + labels */}
            {ticks.map((t) => {
                const y = padT + innerH * (1 - t);
                const val = minY + (maxY - minY) * t;
                return (
                    <g key={t}>
                        <line x1={padL} x2={padL + innerW} y1={y} y2={y} stroke="rgba(0,0,0,0.08)" strokeWidth={1} />
                        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#888">{fmt(val)}</text>
                    </g>
                );
            })}
            {/* X labels (every Nth so it doesn't overlap) */}
            {xLabels.length > 0 ? xLabels.map((lbl, i) => {
                const step = Math.max(1, Math.ceil(xLabels.length / 10));
                if (i % step !== 0 && i !== xLabels.length - 1) return null;
                const x = padL + i * xStep;
                return <text key={i} x={x} y={height - 8} textAnchor="middle" fontSize="9" fill="#888">{lbl}</text>;
            }) : null}
            {/* Series */}
            {series.map((s, si) => {
                const points = s.values.map((v, i) => v == null ? null : [padL + i * xStep, padT + innerH * (1 - (v - minY) / (maxY - minY))]);
                const segments: Array<Array<[number, number]>> = [];
                let cur: Array<[number, number]> = [];
                for (const p of points) {
                    if (p == null) { if (cur.length > 1) segments.push(cur); cur = []; }
                    else cur.push(p as [number, number]);
                }
                if (cur.length > 1) segments.push(cur);
                return (
                    <g key={si}>
                        {segments.map((seg, segi) => {
                            const d = seg.map(([x, y], k) => `${k === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
                            return (
                                <path key={segi} d={d} fill="none" stroke={s.color} strokeWidth={2}
                                    strokeDasharray={s.style === 'dashed' ? '5,5' : undefined} vectorEffect="non-scaling-stroke" />
                            );
                        })}
                        {/* Point dots + tooltips */}
                        {points.map((p, i) => p == null ? null : (
                            <circle key={i} cx={(p as [number, number])[0]} cy={(p as [number, number])[1]} r={2.5} fill={s.color}>
                                <title>{s.name}: {fmt(s.values[i]!)}{xLabels[i] ? ' @ '+xLabels[i] : ''}</title>
                            </circle>
                        ))}
                    </g>
                );
            })}
        </svg>
    );
}

/**
 * Treemap: prikazuje proporcionalne pravokutnike za N kategorija (po SUM).
 * Squared layout aproksimacija (slice-and-dice). Za ~5-12 kategorija odlično.
 */
export function Treemap({
    items, height = 220, locale = 'hr', moneyFormatter,
}: {
    items: Array<{ label: string; value: number; color?: string }>;
    height?: number; locale?: 'hr' | 'en';
    moneyFormatter?: (n: number) => string;
}) {
    const total = items.reduce((s, it) => s + Math.max(0, it.value), 0);
    if (total <= 0 || items.length === 0) return <div style={{ padding: 20, color: '#999', fontStyle: 'italic' }}>Nema podataka.</div>;
    const fmt = moneyFormatter ?? ((n: number) => n.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 0 })+' €');
    // Default golden palette
    const palette = ['#3b82c4', '#1f7a36', '#c99b31', '#8a6817', '#b14040', '#5ca36f', '#d97757', '#6b4f1c', '#7a7a7a', '#1a1a1a'];

    // Simple horizontal split — assumes container width = 100%
    return (
        <div style={{ display: 'flex', width: '100%', height, gap: 1, background: 'rgba(0,0,0,0.04)' }}>
            {items.map((it, i) => {
                const pct = (it.value / total) * 100;
                const color = it.color ?? palette[i % palette.length];
                return (
                    <div key={i} style={{
                        flex: `${pct} 1 0`,
                        background: color,
                        color: '#fff',
                        padding: pct > 5 ? '10px 12px' : '4px 6px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        fontSize: '0.78rem',
                        minWidth: 0,
                    }} title={`${it.label}: ${fmt(it.value)} (${pct.toFixed(1)}%)`}>
                        {pct > 4 ? <>
                            <div style={{ fontWeight: 700, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>{fmt(it.value)}</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{pct.toFixed(1)}%</div>
                        </> : null}
                    </div>
                );
            })}
        </div>
    );
}

export function LineChart({ points, projection = [] }: { points: Point[]; projection?: Array<{ date: string; net: number }> }) {
    const allPoints = [...points, ...projection];
    if (allPoints.length < 2) {
        return <div className="chart-empty">Nema dovoljno podataka za graf.</div>;
    }
    const values = allPoints.map((point) => point.net);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const toPolyline = (items: Array<{ net: number }>, offset = 0) => items.map((point, index) => {
        const x = ((index + offset) / Math.max(allPoints.length - 1, 1)) * 100;
        const y = 88 - ((point.net - min) / range) * 76;
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg className="line-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img">
            <polyline points={toPolyline(points)} fill="none" vectorEffect="non-scaling-stroke" />
            {projection.length > 0 ? <polyline className="projection-line" points={toPolyline(projection, points.length)} fill="none" vectorEffect="non-scaling-stroke" /> : null}
        </svg>
    );
}

export function MiniChart({ points }: { points: Point[] }) {
    return <div className="mini-chart"><LineChart points={points.slice(-24)} /></div>;
}

export type WaterfallBar = { label: string; debit: number; credit: number; net: number };

/**
 * StackedHorizontalBar — jedan red podijeljen u segmente s legendom (npr. AR aging, supplier credit).
 * Svaki segment ima labelu, vrijednost i color. Tooltip pokazuje detalje.
 */
export function StackedHorizontalBar({
    segments, height = 38, locale = 'hr', moneyFormatter, showLegend = true,
}: {
    segments: Array<{ label: string; value: number; color: string }>;
    height?: number; locale?: 'hr' | 'en';
    moneyFormatter?: (n: number) => string;
    showLegend?: boolean;
}) {
    const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
    if (total <= 0) return <div style={{ padding: 10, color: '#999', fontStyle: 'italic', fontSize: '0.82rem' }}>Nema podataka.</div>;
    const fmt = moneyFormatter ?? ((n: number) => n.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 0 })+' €');
    return (
        <div>
            <div style={{ display: 'flex', height, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.04)' }}>
                {segments.map((seg, i) => {
                    const pct = (Math.max(0, seg.value) / total) * 100;
                    if (pct <= 0) return null;
                    return (
                        <div key={i} style={{
                            flex: `${pct} 1 0`, background: seg.color, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.78rem', fontWeight: 600, minWidth: 0, overflow: 'hidden',
                        }} title={`${seg.label}: ${fmt(seg.value)} (${pct.toFixed(1)}%)`}>
                            {pct > 8 ? `${pct.toFixed(0)}%` : ''}
                        </div>
                    );
                })}
            </div>
            {showLegend ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, fontSize: '0.78rem' }}>
                    {segments.map((seg, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 10, height: 10, background: seg.color, borderRadius: 2, display: 'inline-block' }} />
                            <span>{seg.label}</span>
                            <span className="muted">{fmt(seg.value)}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/**
 * RankedBar — više horizontalnih barova, sortirano po vrijednosti, color tier ili custom.
 * Default: largest value u najjačoj boji, ostali skalirani po pct vs largest.
 */
export function RankedBar({
    items, locale = 'hr', moneyFormatter, maxBars = 10, barColor, secondaryFormatter,
}: {
    items: Array<{ label: string; value: number; color?: string; sub?: string }>;
    locale?: 'hr' | 'en';
    moneyFormatter?: (n: number) => string;
    secondaryFormatter?: (it: { label: string; value: number }) => string;
    maxBars?: number;
    barColor?: string;
}) {
    const sorted = [...items].sort((a, b) => Math.abs(b.value) - Math.abs(a.value)).slice(0, maxBars);
    if (sorted.length === 0) return <div style={{ padding: 10, color: '#999', fontStyle: 'italic', fontSize: '0.82rem' }}>Nema podataka.</div>;
    const max = Math.max(1, ...sorted.map((s) => Math.abs(s.value)));
    const fmt = moneyFormatter ?? ((n: number) => n.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 0 })+' €');
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sorted.map((it, i) => {
                const pct = (Math.abs(it.value) / max) * 100;
                const negative = it.value < 0;
                const color = it.color ?? barColor ?? (negative ? '#b14040' : '#1f7a36');
                return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 28%) 1fr auto', gap: 10, alignItems: 'center', fontSize: '0.82rem' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: '#34383d' }} title={it.label}>
                            {it.label}
                            {it.sub ? <div className="muted" style={{ fontSize: '0.7rem', fontWeight: 400 }}>{it.sub}</div> : null}
                        </div>
                        <div style={{ position: 'relative', height: 18, background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                                position: 'absolute', top: 0, left: 0, bottom: 0,
                                width: `${Math.max(2, pct)}%`, background: color, transition: 'width 0.2s',
                            }} />
                        </div>
                        <div style={{ minWidth: 70, textAlign: 'right', fontWeight: 600, color: negative ? '#b14040' : '#1f7a36' }}>
                            {secondaryFormatter ? secondaryFormatter(it) : fmt(it.value)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Heatmap — 2D grid s color scale (low-mid-high). Korisno za RUC matrix kategorija×kanal, kohorte itd.
 * cells: { row, col, value, label? }; rows/cols su osi.
 */
export function Heatmap({
    rows, cols, cells, locale = 'hr', cellFormatter, palette = 'green-red', minLabel, maxLabel,
}: {
    rows: string[]; cols: string[];
    cells: Array<{ row: string; col: string; value: number; label?: string }>;
    locale?: 'hr' | 'en';
    cellFormatter?: (v: number) => string;
    palette?: 'green-red' | 'blue' | 'gold';
    minLabel?: string; maxLabel?: string;
}) {
    if (rows.length === 0 || cols.length === 0) return <div style={{ padding: 10, color: '#999', fontStyle: 'italic', fontSize: '0.82rem' }}>Nema podataka.</div>;
    const lookup = new Map<string, { value: number; label?: string }>();
    for (const c of cells) lookup.set(`${c.row}|${c.col}`, { value: c.value, label: c.label });
    const values = cells.map((c) => c.value);
    const vMin = Math.min(0, ...values);
    const vMax = Math.max(0, ...values);
    const fmt = cellFormatter ?? ((v: number) => v.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 0 }));

    const colorFor = (v: number): string => {
        if (vMax === vMin) return 'rgba(0,0,0,0.05)';
        const t = (v - vMin) / (vMax - vMin); // 0..1
        if (palette === 'green-red') {
            // red (0) -> yellow (0.5) -> green (1)
            const r = Math.round(177 + (60 - 177) * t);
            const g = Math.round(64 + (155 - 64) * t);
            const b = Math.round(64 + (54 - 64) * t);
            return `rgb(${r},${g},${b})`;
        }
        if (palette === 'blue') {
            const a = 0.15 + 0.7 * t;
            return `rgba(59,130,196,${a})`;
        }
        // gold
        const a = 0.15 + 0.75 * t;
        return `rgba(201,155,49,${a})`;
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: '0.78rem' }}>
                <thead>
                    <tr>
                        <th />
                        {cols.map((c) => <th key={c} style={{ padding: '4px 8px', fontWeight: 600, color: '#6b4f1c', whiteSpace: 'nowrap' }}>{c}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r}>
                            <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: '#6b4f1c', whiteSpace: 'nowrap' }}>{r}</th>
                            {cols.map((c) => {
                                const cell = lookup.get(`${r}|${c}`);
                                if (!cell) return <td key={c} style={{ background: 'rgba(0,0,0,0.02)', padding: '6px 10px', minWidth: 56 }}>—</td>;
                                const bg = colorFor(cell.value);
                                const dark = palette === 'green-red' || cell.value === vMax;
                                return (
                                    <td key={c} title={`${r} · ${c}: ${fmt(cell.value)}`} style={{
                                        background: bg, padding: '6px 10px', minWidth: 56, textAlign: 'right',
                                        color: dark ? '#fff' : '#34383d', fontWeight: 600, borderRadius: 3,
                                    }}>
                                        {cell.label ?? fmt(cell.value)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {minLabel || maxLabel ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#888', marginTop: 6 }}>
                    <span>{minLabel ?? fmt(vMin)}</span>
                    <span>{maxLabel ?? fmt(vMax)}</span>
                </div>
            ) : null}
        </div>
    );
}

/**
 * StackedAreaChart — multi-series stacked area (cumulative bottom-up).
 * Idealno za cashflow forecast (opening + inflows - outflows = balance).
 */
export function StackedAreaChart({
    series, xLabels = [], height = 240, locale = 'hr', moneyFormatter,
    overlay,
}: {
    series: Array<{ name: string; color: string; values: number[] }>;
    xLabels?: string[]; height?: number; locale?: 'hr' | 'en';
    moneyFormatter?: (n: number) => string;
    overlay?: { name: string; color: string; values: number[] };
}) {
    if (series.length === 0 || series[0].values.length === 0) return <div style={{ padding: 20, color: '#999', fontStyle: 'italic' }}>Nema podataka.</div>;
    const len = series[0].values.length;
    const stacks: number[][] = [];
    let prev = new Array(len).fill(0);
    for (const s of series) {
        const top = s.values.map((v, i) => prev[i] + Math.max(0, v));
        stacks.push(top);
        prev = top;
    }
    const overlayVals = overlay?.values ?? [];
    const maxY = Math.max(...prev, ...overlayVals, 1);
    const minY = Math.min(0, ...overlayVals);
    const w = 800; const padL = 70; const padR = 12; const padT = 12; const padB = 28;
    const innerW = w - padL - padR; const innerH = height - padT - padB;
    const xStep = innerW / Math.max(len - 1, 1);
    const yFor = (v: number) => padT + innerH * (1 - (v - minY) / (maxY - minY || 1));
    const fmt = moneyFormatter ?? ((n: number) => n >= 1e6 ? (n/1e6).toFixed(1)+'M' : n >= 1e3 ? (n/1e3).toFixed(0)+'k' : String(Math.round(n)));

    const buildPath = (top: number[], bot: number[]): string => {
        const upper = top.map((v, i) => `${i === 0 ? 'M' : 'L'} ${padL + i * xStep} ${yFor(v)}`).join(' ');
        const lower = bot.slice().reverse().map((v, k) => {
            const i = bot.length - 1 - k;
            return `L ${padL + i * xStep} ${yFor(v)}`;
        }).join(' ');
        return `${upper} ${lower} Z`;
    };

    const ticks = [0, 0.25, 0.5, 0.75, 1];

    return (
        <svg viewBox={`0 0 ${w} ${height}`} style={{ width: '100%', height, fontFamily: 'inherit', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet">
            {ticks.map((t) => {
                const y = padT + innerH * (1 - t);
                const val = minY + (maxY - minY) * t;
                return (
                    <g key={t}>
                        <line x1={padL} x2={padL + innerW} y1={y} y2={y} stroke="rgba(0,0,0,0.08)" strokeWidth={1} />
                        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#888">{fmt(val)}</text>
                    </g>
                );
            })}
            {xLabels.length > 0 ? xLabels.map((lbl, i) => {
                const step = Math.max(1, Math.ceil(xLabels.length / 10));
                if (i % step !== 0 && i !== xLabels.length - 1) return null;
                const x = padL + i * xStep;
                return <text key={i} x={x} y={height - 8} textAnchor="middle" fontSize="9" fill="#888">{lbl}</text>;
            }) : null}
            {stacks.map((top, si) => {
                const bot = si === 0 ? new Array(len).fill(0) : stacks[si - 1];
                return <path key={si} d={buildPath(top, bot)} fill={series[si].color} opacity={0.85}>
                    <title>{series[si].name}</title>
                </path>;
            })}
            {overlay ? (() => {
                const d = overlay.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${padL + i * xStep} ${yFor(v)}`).join(' ');
                return <path d={d} fill="none" stroke={overlay.color} strokeWidth={2.5} vectorEffect="non-scaling-stroke">
                    <title>{overlay.name}</title>
                </path>;
            })() : null}
        </svg>
    );
}

/**
 * BulletChart — actual vs target gauge (jedan red).
 * Tonske zone (loše/dobro/odlično) i marker za actual + tanak crni line za target.
 */
export function BulletChart({
    label, actual, target, max, locale = 'hr', valueFormatter, zones, height = 28,
}: {
    label: string;
    actual: number;
    target?: number;
    max: number;
    locale?: 'hr' | 'en';
    valueFormatter?: (n: number) => string;
    zones?: Array<{ until: number; color: string }>; // npr. [{until: max*0.6, color:'#fce4e4'},{until:max*0.9,color:'#fff4d6'},{until:max,color:'#e3f5e8'}]
    height?: number;
}) {
    const fmt = valueFormatter ?? ((n: number) => n.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 1 }));
    const zonesPick = zones ?? [
        { until: max * 0.6, color: '#fce4e4' },
        { until: max * 0.9, color: '#fff4d6' },
        { until: max, color: '#e3f5e8' },
    ];
    const actualPct = Math.max(0, Math.min(100, (actual / max) * 100));
    const targetPct = target != null ? Math.max(0, Math.min(100, (target / max) * 100)) : null;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 28%) 1fr auto', gap: 10, alignItems: 'center', fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 600, color: '#34383d' }}>{label}</div>
            <div style={{ position: 'relative', height, background: 'rgba(0,0,0,0.04)', borderRadius: 5, overflow: 'hidden' }}>
                {zonesPick.map((z, i) => {
                    const prev = i === 0 ? 0 : zonesPick[i - 1].until;
                    const w = ((z.until - prev) / max) * 100;
                    const l = (prev / max) * 100;
                    return <div key={i} style={{ position: 'absolute', left: `${l}%`, width: `${w}%`, top: 0, bottom: 0, background: z.color }} />;
                })}
                <div style={{
                    position: 'absolute', top: '25%', bottom: '25%', left: 0,
                    width: `${actualPct}%`, background: '#34383d', borderRadius: 3,
                }} />
                {targetPct != null ? (
                    <div style={{
                        position: 'absolute', top: 0, bottom: 0, left: `${targetPct}%`,
                        width: 2, background: '#c99b31',
                    }} title={`Target: ${fmt(target!)}`} />
                ) : null}
            </div>
            <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 700, color: '#34383d' }}>
                {fmt(actual)}{target != null ? <span className="muted" style={{ fontSize: '0.7rem', marginLeft: 6, fontWeight: 400 }}>/ {fmt(target)}</span> : null}
            </div>
        </div>
    );
}

/**
 * Sparkline — mini inline line chart (npr. uz svakog partnera trend zadnjih 6-12 točaka).
 */
export function Sparkline({
    values, width = 80, height = 22, color = '#c99b31', filled = true,
}: {
    values: Array<number | null>;
    width?: number; height?: number; color?: string; filled?: boolean;
}) {
    const clean = values.filter((v): v is number => v != null);
    if (clean.length < 2) return <span style={{ display: 'inline-block', width, height, color: '#bbb', fontSize: '0.7rem' }}>—</span>;
    const max = Math.max(...clean); const min = Math.min(...clean);
    const range = max - min || 1;
    const xStep = width / Math.max(values.length - 1, 1);
    const points = values.map((v, i) => v == null ? null : [i * xStep, height - ((v - min) / range) * height]);
    const segments: Array<Array<[number, number]>> = [];
    let cur: Array<[number, number]> = [];
    for (const p of points) {
        if (p == null) { if (cur.length > 1) segments.push(cur); cur = []; }
        else cur.push(p as [number, number]);
    }
    if (cur.length > 1) segments.push(cur);
    const lineD = segments.map((seg) => seg.map(([x, y], k) => `${k === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ')).join(' ');
    const areaD = segments.map((seg) => {
        const path = seg.map(([x, y], k) => `${k === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
        const last = seg[seg.length - 1][0]; const first = seg[0][0];
        return `${path} L ${last} ${height} L ${first} ${height} Z`;
    }).join(' ');
    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width, height, display: 'inline-block', verticalAlign: 'middle' }}>
            {filled ? <path d={areaD} fill={color} opacity={0.18} /> : null}
            <path d={lineD} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

export function WaterfallChart({ bars }: { bars: WaterfallBar[] }) {
    if (bars.length === 0) {
        return <div className="chart-empty">Nema mjesečnih podataka.</div>;
    }
    const maxAbs = Math.max(1, ...bars.map((b) => Math.max(Math.abs(b.debit), Math.abs(b.credit), Math.abs(b.net))));
    const barW = 100 / (bars.length * 3 + (bars.length - 1));
    return (
        <svg className="waterfall-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img">
            <line x1="0" x2="100" y1="50" y2="50" className="waterfall-axis" />
            {bars.map((b, i) => {
                const baseX = i * (3 * barW + barW);
                const hCredit = (Math.abs(b.credit) / maxAbs) * 45;
                const hDebit = (Math.abs(b.debit) / maxAbs) * 45;
                const hNet = (Math.abs(b.net) / maxAbs) * 45;
                return (
                    <g key={b.label}>
                        <rect x={baseX} y={50 - hCredit} width={barW} height={hCredit} className="waterfall-credit" />
                        <rect x={baseX + barW} y={50} width={barW} height={hDebit} className="waterfall-debit" />
                        <rect x={baseX + 2 * barW} y={b.net >= 0 ? 50 - hNet : 50} width={barW} height={hNet} className={b.net >= 0 ? 'waterfall-net-pos' : 'waterfall-net-neg'} />
                        <text x={baseX + (3 * barW) / 2} y={98} className="waterfall-label">{b.label}</text>
                    </g>
                );
            })}
        </svg>
    );
}
